import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { getAssumedUser } from 'lib/auth/impersonation'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import { PageHeader } from 'components/page-header'
import StarIcon from 'components/material-symbol/icons/Star'
import Error from 'components/error'
import {
  BENCHMARK_FOOTER,
  BENCHMARK_METRICS,
  BENCHMARK_SAMPLE_DATA_NOTICE,
  BENCHMARK_USES_SAMPLE_METRICS,
  COACH_DASHBOARD_PATH,
  FASTEST_WIN_BANNER,
  parseBenchmarkMetricId,
} from 'pages/pga-coach/dashboard/performance-benchmarks/constants'
import { getRecommendedStepCompletionMap } from 'pages/pga-coach/dashboard/performance-benchmarks/recommended-step-completion'
import CoachPlusPromoBanner from 'pages/pga-coach/dashboard/performance-benchmarks/benchmark-promo'
import BenchmarkDetailCard from './benchmark-detail-card'
import GET_BENCHMARK_RECOMMENDED_STEP_DATA from './query'
import useStyles from './styles'

const BenchmarksPage = () => {
  const classes = useStyles({ assumedUser: getAssumedUser() })
  const { search } = useLocation()
  const expandedMetricId = parseBenchmarkMetricId(search)
  const { data, loading, error } = useQuery(GET_BENCHMARK_RECOMMENDED_STEP_DATA)

  const recommendedStepCompletion = useMemo(() => {
    if (!data) {
      return {}
    }

    const coachAttributes = data.currentUser?.attributes

    return getRecommendedStepCompletionMap({
      preferences: coachAttributes?.preferences,
      groupEvents: data.currentUser?.groupEvents,
      lessonTypes: data.lessonTypes,
      coachAvailabilities: data.coachAvailabilities,
      coachingInquiries: data.coachingInquiries,
    })
  }, [data])

  useEffect(() => {
    if (!expandedMetricId) {
      return
    }

    const element = document.getElementById(
      `benchmark-metric-${expandedMetricId}`,
    )
    element?.scrollIntoView?.({ block: 'start' })
  }, [expandedMetricId])

  if (error) {
    return <Error error={error} />
  }

  return (
    <>
      <PageHeader
        title="Performance benchmarks"
        backTo={COACH_DASHBOARD_PATH}
      />
      {loading && !data ? <LinearProgress color="primary" /> : null}
      <Box className={classes.container}>
        <CoachPlusPromoBanner
          className={classes.promoBanner}
          variant="coachPlus"
          eyebrow={FASTEST_WIN_BANNER.badge}
          eyebrowIcon={StarIcon}
          title={FASTEST_WIN_BANNER.title}
          body={FASTEST_WIN_BANNER.body}
          actionLabel={FASTEST_WIN_BANNER.actionLabel}
          actionPath={FASTEST_WIN_BANNER.actionPath}
        />

        {BENCHMARK_USES_SAMPLE_METRICS ? (
          <Typography className={classes.sampleNotice}>
            {BENCHMARK_SAMPLE_DATA_NOTICE}
          </Typography>
        ) : null}

        <Box className={classes.metricListCard}>
          {BENCHMARK_METRICS.map(metric => (
            <BenchmarkDetailCard
              key={metric.id}
              metric={metric}
              classes={classes}
              defaultExpanded={metric.id === expandedMetricId}
              recommendedStepCompletion={recommendedStepCompletion}
            />
          ))}
        </Box>

        <Typography className={classes.footer}>{BENCHMARK_FOOTER}</Typography>
      </Box>
    </>
  )
}

export default BenchmarksPage
